import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  PencilIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../lib/api';

interface Template {
  id: string;
  name: string;
  description?: string;
  template_type: 'email' | 'sms';
  status: 'draft' | 'active' | 'archived';
  subject?: string;
  content: string;
  variables?: string[];
  preview_data?: Record<string, any>;
}

interface TemplatePreviewModalProps {
  templateId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onUseInCampaign?: (templateId: string) => void;
}

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  templateId,
  isOpen,
  onClose,
  onUseInCampaign,
}) => {
  // Don't render if not open or no templateId
  if (!isOpen || !templateId) {
    return null;
  }
  const navigate = useNavigate();
  const [showRendered, setShowRendered] = useState(false);

  // Fetch template
  const { data: template, isLoading, error } = useQuery<Template>({
    queryKey: ['template', templateId],
    queryFn: async () => {
      const response = await api.templates.get(templateId);
      return response.data;
    },
    enabled: !!templateId,
  });

  // Fetch rendered preview
  const { data: renderedPreview, isLoading: isLoadingPreview } = useQuery({
    queryKey: ['template-preview', templateId],
    queryFn: async () => {
      const sampleData = {
        contact: {
          first_name: 'John',
          last_name: 'Doe',
          email: 'john.doe@example.com',
          phone: '+1234567890',
          company: 'Acme Corp',
        },
        tenant: {
          name: 'Your Business',
          domain: 'yourbusiness.com',
        },
        system: {
          unsubscribe_url: 'https://example.com/unsubscribe',
          company_name: 'Your Business',
        },
      };
      const response = await api.templates.preview(templateId, sampleData);
      return response.data;
    },
    enabled: !!templateId && showRendered,
  });

  // Highlight variables in content
  const highlightVariables = (text: string) => {
    if (!text) return text;
    
    const parts = text.split(/(\{\{[^}]+\}\})/g);
    return parts.map((part, index) => {
      if (part.match(/\{\{[^}]+\}\}/)) {
        return (
          <span key={index} className="bg-yellow-100 text-yellow-800 px-1 rounded font-mono text-sm">
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Character count (especially for SMS)
  const getCharacterCount = (text: string) => {
    return text?.length || 0;
  };

  const getSMSSegments = (text: string) => {
    const length = getCharacterCount(text);
    if (length <= 160) return 1;
    return Math.ceil(length / 153);
  };

  // Handle use in campaign
  const handleUseInCampaign = () => {
    if (onUseInCampaign) {
      onUseInCampaign(templateId);
    } else {
      // Navigate to campaign builder with template pre-selected
      navigate(`/campaigns/new?template=${templateId}`);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>

        {/* Modal content */}
        <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:align-middle">
          {/* Loading State */}
          {isLoading && (
            <div className="px-4 pt-5 pb-4 sm:p-6">
              <div className="flex items-center justify-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <p className="ml-3 text-sm text-gray-500">Loading template...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="px-4 pt-5 pb-4 sm:p-6">
              <div className="rounded-md bg-red-50 p-4">
                <h3 className="text-sm font-medium text-red-800">Error loading template</h3>
                <p className="mt-2 text-sm text-red-700">
                  {(error as any)?.message || 'Template not found'}
                </p>
              </div>
              <div className="mt-4">
                <button
                  onClick={onClose}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* Template Content */}
          {template && (
            <>
              {/* Header */}
              <div className="bg-gray-50 px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium leading-6 text-gray-900 flex items-center">
                      {template.template_type === 'email' ? (
                        <EnvelopeIcon className="h-5 w-5 mr-2 text-purple-600" />
                      ) : (
                        <DevicePhoneMobileIcon className="h-5 w-5 mr-2 text-indigo-600" />
                      )}
                      {template.name}
                    </h3>
                    {template.description && (
                      <p className="mt-1 text-sm text-gray-500">{template.description}</p>
                    )}
                    <div className="mt-2 flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        template.template_type === 'email' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-indigo-100 text-indigo-800'
                      }`}>
                        {template.template_type.toUpperCase()}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        template.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : template.status === 'draft'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {template.status.charAt(0).toUpperCase() + template.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="ml-3 rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="px-4 pt-5 pb-4 sm:p-6">
                {/* View Toggle */}
                <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-4">
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setShowRendered(false)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        !showRendered
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Raw Content
                    </button>
                    <button
                      onClick={() => setShowRendered(true)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        showRendered
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <SparklesIcon className="h-4 w-4 inline mr-1" />
                      Rendered Preview
                    </button>
                  </div>
                </div>

                {/* Raw Content View */}
                {!showRendered && (
                  <div className="space-y-4">
                    {/* Subject (Email only) */}
                    {template.template_type === 'email' && template.subject && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Subject
                        </label>
                        <div className="bg-gray-50 rounded-md p-3 text-sm">
                          {highlightVariables(template.subject)}
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Content
                      </label>
                      <div className="bg-gray-50 rounded-md p-4 text-sm whitespace-pre-wrap font-mono max-h-96 overflow-y-auto">
                        {highlightVariables(template.content)}
                      </div>
                    </div>

                    {/* Character Count (SMS) */}
                    {template.template_type === 'sms' && (
                      <div className="bg-blue-50 rounded-md p-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-blue-700">
                            Character Count: <strong>{getCharacterCount(template.content)}</strong>
                          </span>
                          <span className="text-blue-700">
                            SMS Segments: <strong>{getSMSSegments(template.content)}</strong>
                          </span>
                        </div>
                        {getCharacterCount(template.content) > 160 && (
                          <p className="mt-2 text-xs text-blue-600">
                            ⚠️ Messages over 160 characters will be sent as multiple segments
                          </p>
                        )}
                      </div>
                    )}

                    {/* Variables */}
                    {template.variables && template.variables.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Variables ({template.variables.length})
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {template.variables.map((variable, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 font-mono"
                            >
                              {variable}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Rendered Preview View */}
                {showRendered && (
                  <div className="space-y-4">
                    {isLoadingPreview ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <p className="ml-3 text-sm text-gray-500">Rendering preview...</p>
                      </div>
                    ) : renderedPreview ? (
                      <>
                        {/* Rendered Subject (Email only) */}
                        {template.template_type === 'email' && renderedPreview.rendered_subject && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Subject (Rendered)
                            </label>
                            <div className="bg-gray-50 rounded-md p-3 text-sm font-semibold">
                              {renderedPreview.rendered_subject}
                            </div>
                          </div>
                        )}

                        {/* Rendered Content */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Content (Rendered)
                          </label>
                          <div className="bg-white border border-gray-200 rounded-md p-4 text-sm max-h-96 overflow-y-auto">
                            {template.template_type === 'email' ? (
                              <div dangerouslySetInnerHTML={{ __html: renderedPreview.rendered_content }} />
                            ) : (
                              <div className="whitespace-pre-wrap">{renderedPreview.rendered_content}</div>
                            )}
                          </div>
                        </div>

                        {/* Sample Data Used */}
                        <div className="bg-blue-50 rounded-md p-3">
                          <p className="text-xs text-blue-700">
                            <strong>Sample data used:</strong> John Doe (john.doe@example.com)
                          </p>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <p>Preview not available</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={handleUseInCampaign}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Use in Campaign
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/templates/${templateId}/edit`)}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit Template
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

